import React from "react";

/**
 * DataTable - Tabla genérica paginada con soporte de filtros y ordenamiento.
 *
 * Props:
 * - columns: Array<{ key: string, label: string, render?: (row) => ReactNode, sortable?: boolean }>
 * - data: Array<object> - Filas de datos
 * - loading: boolean - Estado de carga (muestra skeleton)
 * - emptyMessage: string - Mensaje cuando no hay datos
 * - pagination: { limit: number, skip: number, total: number } | null
 * - onPageChange: (skip: number) => void - Callback de cambio de página
 * - onSort: (key: string, direction: 'asc'|'desc') => void (optional)
 * - sortKey: string (optional) - Columna actualmente ordenada
 * - sortDirection: 'asc'|'desc' (optional)
 * - pageOptions: number[] (optional) - Opciones de registros por página (default: [50, 100, 200])
 * - onPageSizeChange: (limit: number) => void (optional)
 */
export function DataTable({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = "No hay datos disponibles.",
  pagination = null,
  onPageChange,
  onSort,
  sortKey,
  sortDirection,
  pageOptions = [50, 100, 200],
  onPageSizeChange,
}) {
  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.limit)
    : 0;
  const currentPage = pagination
    ? Math.floor(pagination.skip / pagination.limit)
    : 0;

  const handleSort = (col) => {
    if (!col.sortable || !onSort) return;
    const newDirection =
      sortKey === col.key && sortDirection === "asc" ? "desc" : "asc";
    onSort(col.key, newDirection);
  };

  const handlePageClick = (page) => {
    if (onPageChange && pagination) {
      onPageChange(page * pagination.limit);
    }
  };

  if (loading) {
    return (
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={`skeleton-${i}`} className="animate-pulse">
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    col.sortable ? "cursor-pointer select-none hover:bg-gray-100" : ""
                  }`}
                  onClick={() => handleSort(col)}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && sortKey === col.key && (
                      <span className="text-gray-700">
                        {sortDirection === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, i) => (
                <tr
                  key={row.id || i}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap"
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {pagination && pagination.total > 0 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>
              Mostrando {pagination.skip + 1}–
              {Math.min(pagination.skip + pagination.limit, pagination.total)} de{" "}
              {pagination.total}
            </span>
            {onPageSizeChange && (
              <select
                value={pagination.limit}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                {pageOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt} por página
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              disabled={currentPage === 0}
              onClick={() => handlePageClick(currentPage - 1)}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            {renderPageButtons(currentPage, totalPages, handlePageClick)}
            <button
              disabled={currentPage >= totalPages - 1}
              onClick={() => handlePageClick(currentPage + 1)}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Renders a compact set of page buttons with ellipsis for large page counts.
 */
function renderPageButtons(currentPage, totalPages, onPageClick) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => (
      <button
        key={i}
        onClick={() => onPageClick(i)}
        className={`px-3 py-1 text-sm border rounded ${
          currentPage === i
            ? "bg-purple text-white border-purple"
            : "border-gray-300 hover:bg-gray-100"
        }`}
      >
        {i + 1}
      </button>
    ));
  }

  const pages = [];
  const addPage = (i) => {
    pages.push(
      <button
        key={i}
        onClick={() => onPageClick(i)}
        className={`px-3 py-1 text-sm border rounded ${
          currentPage === i
            ? "bg-purple text-white border-purple"
            : "border-gray-300 hover:bg-gray-100"
        }`}
      >
        {i + 1}
      </button>
    );
  };

  addPage(0);

  if (currentPage > 2) {
    pages.push(
      <span key="start-ellipsis" className="px-1 text-gray-500">
        …
      </span>
    );
  }

  const start = Math.max(1, currentPage - 1);
  const end = Math.min(totalPages - 2, currentPage + 1);
  for (let i = start; i <= end; i++) {
    addPage(i);
  }

  if (currentPage < totalPages - 3) {
    pages.push(
      <span key="end-ellipsis" className="px-1 text-gray-500">
        …
      </span>
    );
  }

  addPage(totalPages - 1);

  return pages;
}

export default DataTable;
